import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Minus, Plus, ShoppingCart, AlertCircle } from "lucide-react";
import api from "../api";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import ProductImage from "../components/ProductImage";
import useNoIndex from "../hooks/useNoIndex";
import Wholesale from "./Wholesale";

const KIT_SIZE = 10;

/**
 * /wholesale-shop/:slug — wholesale product page. Forces quantity in
 * multiples of 10 (one kit). Adds `is_wholesale: true` items to cart.
 */
export default function WholesaleProductDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [variant, setVariant] = useState(null);
  const [kits, setKits] = useState(1);
  const [err, setErr] = useState("");
  const { addItem } = useCart();
  useNoIndex();

  const approved = !!(user?.wholesale_approved || user?.role === "admin");

  useEffect(() => {
    if (!approved) return;
    api.get(`/wholesale/products/${slug}`)
      .then(({ data }) => {
        setProduct(data);
        setVariant(data.variants?.[0] || null);
      })
      .catch((e) => setErr(e?.response?.data?.detail || "Product not found."));
  }, [slug, approved]);

  if (!user) {
    return <Gate message="Please sign in to access wholesale pricing." />;
  }
  if (!approved) {
    return <Wholesale />;
  }
  if (err) return <Gate message={err} cta={{ to: "/wholesale-shop", label: "Back to Wholesale Catalog" }} />;
  if (!product || !variant) {
    return <div className="max-w-7xl mx-auto px-6 py-32 text-center text-[#8A94A6] font-mono-tech text-sm">Loading…</div>;
  }

  const vialsPerKit = KIT_SIZE;
  const totalVials = kits * vialsPerKit;
  const unitPrice = Number(variant.wholesale_price || 0);
  const kitPrice = unitPrice * vialsPerKit;
  const lineTotal = kitPrice * kits;

  const add = () => {
    addItem(product, variant, totalVials, { wholesale: true });
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-16" data-testid="wholesale-product-detail">
      <div className="text-[10px] font-mono-tech text-[#5C657A] uppercase tracking-wider mb-6">
        <Link to="/wholesale-shop" className="hover:text-white">Wholesale Catalog</Link>
        {" / "}{product.category}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
        <div className="vial-bg aspect-square flex items-center justify-center">
          <ProductImage product={product} className="w-[68%] h-[88%]" />
        </div>

        <div className="flex flex-col">
          <div className="overline">WHOLESALE · {product.category}</div>
          <h1 data-testid="wholesale-product-name" className="font-heading text-4xl sm:text-5xl font-light leading-tight mt-3">
            {product.name}
          </h1>
          <p className="text-sm text-[#8A94A6] mt-6 leading-relaxed max-w-md">
            {product.summary}
          </p>

          <div className="mt-10">
            <div className="overline mb-4">Select Size</div>
            <div className={`grid gap-px bg-white/10 border border-white/10 ${product.variants.length >= 3 ? "grid-cols-3" : "grid-cols-2"}`}>
              {product.variants.map((v) => (
                <button
                  key={v.size}
                  data-testid={`wholesale-variant-${v.size}`}
                  onClick={() => setVariant(v)}
                  className={`bg-[#0C0C12] p-4 transition-colors text-left ${
                    variant?.size === v.size ? "ring-1 ring-[#00C3FF] z-10" : "hover:bg-[#14141C]"
                  }`}
                >
                  <div className="font-mono-tech text-base uppercase">{v.size}</div>
                  <div className="font-mono-tech text-xs text-[#00C3FF] mt-1">
                    ${(Number(v.wholesale_price) * KIT_SIZE).toFixed(2)} / kit
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="mt-10 p-5 border border-[#00C3FF]/30 bg-[#00C3FF]/5">
            <div className="overline mb-3">KIT PRICING · {variant.size}</div>
            <div className="flex items-end justify-between gap-4">
              <div>
                <div className="text-[10px] font-mono-tech text-[#5C657A] uppercase tracking-wider">Per Kit</div>
                <div className="text-[10px] font-mono-tech text-[#5C657A] tracking-wider mt-0.5">{vialsPerKit} vials · same SKU</div>
              </div>
              <div className="font-mono-tech text-3xl text-[#00C3FF]">${kitPrice.toFixed(2)}</div>
            </div>
          </div>

          <div className="mt-8">
            <div className="overline mb-4">Quantity (Kits)</div>
            <div className="flex items-center gap-4">
              <button
                data-testid="wholesale-qty-minus"
                onClick={() => setKits(Math.max(1, kits - 1))}
                className="p-3 border border-white/10 hover:border-[#00C3FF] transition-colors"
              >
                <Minus size={14} />
              </button>
              <div className="font-mono-tech text-3xl text-white min-w-[80px] text-center" data-testid="wholesale-kits">
                {kits}
              </div>
              <button
                data-testid="wholesale-qty-plus"
                onClick={() => setKits(kits + 1)}
                className="p-3 border border-white/10 hover:border-[#00C3FF] transition-colors"
              >
                <Plus size={14} />
              </button>
              <div className="text-[10px] font-mono-tech text-[#5C657A] ml-4 uppercase tracking-wider">
                {kits === 1 ? "1 kit" : `${kits} kits`}
              </div>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-white/5 flex items-baseline justify-between">
            <div className="overline">LINE TOTAL</div>
            <div className="font-mono-tech text-3xl text-[#00C3FF]" data-testid="wholesale-line-total">
              ${lineTotal.toFixed(2)}
            </div>
          </div>

          <button
            data-testid="wholesale-add-to-cart"
            onClick={add}
            className="btn-primary mt-6 w-full justify-center"
          >
            <ShoppingCart size={16} /> Add {kits} Kit{kits !== 1 ? "s" : ""} to Wholesale Cart
          </button>

          <div className="mt-4 text-[10px] font-mono-tech text-[#5C657A] uppercase tracking-wider text-center">
            Minimum 5 kits total across cart · mix &amp; match any products · For Research Use Only
          </div>

          <div className="mt-6 p-4 border border-yellow-500/20 bg-yellow-500/5 flex gap-3">
            <AlertCircle size={14} className="text-yellow-400 flex-shrink-0 mt-0.5" />
            <div className="text-[10px] text-[#8A94A6] leading-relaxed">
              Wholesale and retail items cannot be mixed in one order — adding wholesale items
              will clear any retail items in your cart, and vice versa.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Gate({ message, cta }) {
  return (
    <div className="max-w-2xl mx-auto px-6 py-32 text-center">
      <div className="surface p-12">
        <div className="overline">WHOLESALE</div>
        <h1 className="font-heading text-3xl mt-3">Access required</h1>
        <p className="text-sm text-[#8A94A6] mt-4 leading-relaxed">{message}</p>
        {cta && (
          <Link to={cta.to} className="btn-primary mt-8 inline-flex">
            {cta.label}
          </Link>
        )}
      </div>
    </div>
  );
}
